/**
 * OQ-07 deliverable #0: stage-qualified composer anchors.
 * Spine hrefs are wired when CARE_PLAN_COMPOSER_STREAMLINED is on.
 * Bare `#fragment` hrefs remain the flag-off monolith contract.
 */
import { CARE_PLAN_SUBMIT_FOR_REVIEW_ANCHOR } from '@/lib/care-plans/care-plan-acknowledgement-resolution';
import { PLAN_SHAPE_ANCHOR } from '@/lib/care-plans/care-plan-blocker-resolution';
import type { ComposerPageAnchor } from '@/lib/care-plans/composer-anchor-navigation';
import {
  CARE_PLAN_STAGE_IDS,
  type CarePlanStageId,
} from '@/lib/client-care-redesign/care-plan-stage-api';

export const COMPOSER_RETURN_PARAM_KEYS = [
  'from',
  'clientId',
  'returnTab',
  'returnHash',
  'addServiceCode',
  'frequencyPerQuarter',
  'unitCost',
] as const;

export type ComposerReturnParamKey = (typeof COMPOSER_RETURN_PARAM_KEYS)[number];

export const COMPOSER_STAGE_QUERY_KEY = 'stage';

const ANCHOR_STAGE: Record<string, CarePlanStageId> = {
  'composer-participation': 'understand',
  'import-inbox': 'understand',
  'composer-assess': 'understand',
  support_profile: 'understand',
  medical_status_flags: 'understand',
  reviews: 'understand',
  an_acc_classification: 'plan',
  rac_care_minutes: 'plan',
  clinical_handover: 'plan',
  daily_care_evidence: 'plan',
  restrictive_practice: 'plan',
  food_nutrition: 'plan',
  residential_rounds: 'plan',
  goals: 'plan',
  risks: 'plan',
  interventions: 'plan',
  preferences: 'plan',
  worker_instructions: 'plan',
  [PLAN_SHAPE_ANCHOR]: 'plan',
  services: 'services-funding',
  budget: 'services-funding',
  'composer-agree': 'agree',
  'composer-activate': 'activate',
  [CARE_PLAN_SUBMIT_FOR_REVIEW_ANCHOR]: 'activate',
};

export function composerStageForAnchor(anchor: string | null | undefined): CarePlanStageId | null {
  if (!anchor) return null;
  const trimmed = anchor.replace(/^#/, '').trim();
  if ((CARE_PLAN_STAGE_IDS as readonly string[]).includes(trimmed)) {
    return trimmed as CarePlanStageId;
  }
  return ANCHOR_STAGE[trimmed] ?? null;
}

/** Unmapped anchors must not be treated as Understand. Callers fail closed. */

export function composerStageQualifiedSearch(
  current: URLSearchParams,
  stage: CarePlanStageId,
): URLSearchParams {
  const next = new URLSearchParams(current.toString());
  next.set(COMPOSER_STAGE_QUERY_KEY, stage);
  return next;
}

/**
 * Build a compose href that sets `?stage=` without dropping return context.
 * Does not invent missing return params.
 */
export function composerStageQualifiedHref(input: {
  pathname: string;
  currentSearch: URLSearchParams;
  stage: CarePlanStageId;
  hash?: string | null;
}): string {
  const search = composerStageQualifiedSearch(input.currentSearch, input.stage);
  const query = search.toString();
  const hash = input.hash ? `#${input.hash.replace(/^#/, '')}` : '';
  return `${input.pathname}${query ? `?${query}` : ''}${hash}`;
}

export function composerReturnParamsPreserved(
  before: URLSearchParams,
  after: URLSearchParams,
): boolean {
  return COMPOSER_RETURN_PARAM_KEYS.every((key) => before.get(key) === after.get(key));
}

export function parseComposerStageFromSearch(search: URLSearchParams): CarePlanStageId | null {
  const raw = search.get(COMPOSER_STAGE_QUERY_KEY);
  if (!raw) return null;
  if ((CARE_PLAN_STAGE_IDS as readonly string[]).includes(raw)) {
    return raw as CarePlanStageId;
  }
  return composerStageForAnchor(raw);
}

export function resolveComposerActiveStage(input: {
  requested: CarePlanStageId | null;
  fallbackAnchor?: string | null;
  firstStageAnchor?: string | null;
}): CarePlanStageId {
  return (
    input.requested ??
    composerStageForAnchor(input.fallbackAnchor) ??
    composerStageForAnchor(input.firstStageAnchor) ??
    'understand'
  );
}

export function shouldMountComposerStage(
  streamlined: boolean,
  activeStage: CarePlanStageId | null,
  stage: CarePlanStageId,
): boolean {
  if (!streamlined) return true;
  return activeStage === stage;
}

export function composerStageQualifiedActionHref(input: {
  pathname: string;
  currentSearch: URLSearchParams;
  actionHref: string;
}): string {
  const raw = input.actionHref.trim();
  const hashIndex = raw.indexOf('#');
  const hash = hashIndex >= 0 ? raw.slice(hashIndex + 1) : raw.replace(/^\//, '');
  const stage = composerStageForAnchor(hash);
  if (!stage) return raw;
  return composerStageQualifiedHref({
    pathname: input.pathname,
    currentSearch: input.currentSearch,
    stage,
    hash,
  });
}

/**
 * Next-task Open lands on the named next stage.
 * Blocker hashes that resolve to a different stage (EOL → Activate) stay off this CTA.
 */
export function composerNextStageHref(input: {
  pathname: string;
  currentSearch: URLSearchParams;
  nextStageId: string;
}): string {
  const id = input.nextStageId.replace(/^#/, '').trim();
  const stage = composerStageForAnchor(id);
  if (!stage) return id ? `#${id}` : '#';
  return composerStageQualifiedHref({
    pathname: input.pathname,
    currentSearch: input.currentSearch,
    stage,
    hash: id,
  });
}
